/**
 * Chrome shared by both Player Stats views: page head and garden toggle.
 *
 * The section tabs live in each view rather than here, because the design puts a
 * view-specific control bar (scope pickers on the table, the player header on the
 * player page) *above* them — rendering the tabs in the layout would force that
 * bar below them.
 *
 * Everything selectable lives in the URL, so a view is always a link someone can
 * paste into Slack, and the two views share state without a store.
 */

import { createContext, useContext } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { Outlet } from "react-router-dom";

import { usePageTitle } from "../../../hooks/usePageTitle";
import {
  PlayerStatSection,
  PlayerStatsGarden,
  PlayerStatsScope,
} from "../../../models/PlayerStats";
import { useGarden } from "./PlayerStats";
import { usePlayerStatsScopes } from "./usePlayerStats";

const GARDENS: Record<
  PlayerStatsGarden,
  { title: string; defaultLeague: string }
> = {
  nba: { title: "NBA Player Statistics", defaultLeague: "NBA" },
  prospect: { title: "Draft Prospect Statistics", defaultLeague: "NCAA" },
};

interface PlayerStatsContextValue {
  garden: PlayerStatsGarden;
  /** Sections this garden serves. Prospect blobs carry no impact data, so the
   *  backend omits those sections — the page never has to know why. */
  sections: PlayerStatSection[];
  leagues: string[];
  scopes: PlayerStatsScope[];
  defaultLeague: string;
  loading: boolean;
}

const Ctx = createContext<PlayerStatsContextValue | null>(null);

export function usePlayerStatsContext(): PlayerStatsContextValue {
  const value = useContext(Ctx);
  if (!value) {
    throw new Error(
      "usePlayerStatsContext must be used within PlayerStatsLayout",
    );
  }
  return value;
}

/** Season-year to the two-year label coaches read in, e.g. 2025 -> 2025-26. */
export function seasonLabel(season: number | null): string {
  if (season == null) return "—";
  return `${season}–${String((season + 1) % 100).padStart(2, "0")}`;
}

export default function PlayerStatsLayout() {
  const garden = useGarden();
  const config = GARDENS[garden];
  const { data, loading, error } = usePlayerStatsScopes(garden);

  usePageTitle(`${config.title} - Explorer`);

  return (
    <Ctx.Provider
      value={{
        garden,
        sections: data?.sections ?? [],
        leagues: data?.leagues ?? [],
        scopes: data?.scopes ?? [],
        defaultLeague: config.defaultLeague,
        loading,
      }}
    >
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        sx={{ backgroundColor: "var(--background-gray)" }}
      >
        <Box px={2.5} pt={1.75}>
          <Stack direction="row" alignItems="flex-end" spacing={2} mb={1.25}>
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "#8a8a8a",
                  mb: 0.5,
                }}
              >
                EXPLORER › PLAYER STATS
              </Typography>
              <Typography variant="h2" sx={{ fontSize: 28, lineHeight: 1 }}>
                {config.title}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          px={2.5}
          pb={2}
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
        >
          {error ? <Alert severity="error">{error}</Alert> : <Outlet />}
        </Box>
      </Box>
    </Ctx.Provider>
  );
}
